import React, { useEffect } from 'react';
import { PageStoreExtractor, UrlDetailsExtractor } from '../../context/StoreContext';
import { Component, ComponentPropertyDefinition, ComponentProps } from '../../types/common';
import { processComponentStylePseudoClasses } from '../../util/styleProcessor';
import Children from '../Children';
import { HelperComponent } from '../HelperComponents/HelperComponent';
import { SubHelperComponent } from '../HelperComponents/SubHelperComponent';
import useDefinition from '../util/useDefinition';
import AnimatorStyle from './AnimatorStyle';
import { propertiesDefinition, stylePropertiesDefinition } from './animatorProperties';
import { styleProperties, styleDefaults } from './animatorStyleProperties';
import { IconHelper } from '../util/IconHelper';

function makeAnimationString(animations: any[]): string {
	if (!animations?.length) return '';
	return animations
		.filter(a => a.observation === 'none')
		.map(a => makeOneAnimationString(a))
		.filter(a => !!a)
		.join(', ');
}

function makeOneAnimationString(a: any): string {
	if (!a.condition) return '';
	return `${a.animationName} ${a.animationDuration}ms ${a.animationTimingFunction}${
		a.animationTimingFunction === 'cubic-bezier' || a.animationTimingFunction === 'steps'
			? `(${a.timingFunctionExtra})`
			: ''
	} ${a.animationDelay}ms ${a.animationIterationCount} ${a.animationDirection} ${
		a.animationFillMode
	}`;
}

function Animator(props: Readonly<ComponentProps>) {
	const { definition, pageDefinition, locationHistory, context } = props;

	const pageExtractor = PageStoreExtractor.getForContext(context.pageName);
	const urlExtractor = UrlDetailsExtractor.getForContext(context.pageName);
	const {
		key,
		stylePropertiesWithPseudoStates,
		properties: { animation = [] } = {},
	} = useDefinition(
		definition,
		propertiesDefinition,
		stylePropertiesDefinition,
		locationHistory,
		pageExtractor,
		urlExtractor,
	);

	const childs = (
		<Children
			key={`${key}_chld`}
			pageDefinition={pageDefinition}
			renderableChildren={definition.children}
			context={context}
			locationHistory={locationHistory}
		/>
	);

	const resolvedStyles = processComponentStylePseudoClasses(
		props.pageDefinition,
		{},
		stylePropertiesWithPseudoStates,
	);

	const [observations, setObservations] = React.useState<any[]>([]);

	const ref = React.useRef<HTMLDivElement>(null);

	const animationCount = React.useRef<{ [key: string]: number }>({});

	useEffect(() => {
		console.log('[Animator] effect run', { key, animation, hasRef: !!ref.current });
		if (!animation?.length || !ref.current) {
			console.log('[Animator] bailing — no animations or no ref', {
				key,
				animationsLen: animation?.length,
				hasRef: !!ref.current,
			});
			return;
		}

		const threshold: number[] = [];

		const entering: Map<number, any[]> = new Map();
		const exiting: Map<number, any[]> = new Map();

		for (let each of animation) {
			if (each.observation === 'none') continue;
			if (each.observation === 'entering') {
				if (!entering.has(each.enteringThreshold)) entering.set(each.enteringThreshold, []);
				entering.get(each.enteringThreshold)!.push(each);
				threshold.push(each.enteringThreshold);
			} else if (each.observation === 'exiting') {
				const th = each.exitingThreshold;
				if (!exiting.has(th)) exiting.set(th, []);
				exiting.get(th)!.push(each);
				threshold.push(th);
			}
		}

		console.log('[Animator] observer setup', {
			key,
			thresholds: Array.from(new Set(threshold)),
			enteringKeys: Array.from(entering.keys()),
			exitingKeys: Array.from(exiting.keys()),
			enteringCount: Array.from(entering.values()).reduce((s, a) => s + a.length, 0),
			exitingCount: Array.from(exiting.values()).reduce((s, a) => s + a.length, 0),
		});

		try {
			const io = new IntersectionObserver(
				entries => {
					console.log('[Animator] IO fired', {
						key,
						entriesCount: entries.length,
					});
					if (entries.length !== 1) {
						console.log('[Animator] skipping — entries.length !== 1', {
							key,
							entriesCount: entries.length,
						});
						return;
					}
					const entry = entries[0];

					let isEntering =
						entry.boundingClientRect.top >= 0 &&
						entry.boundingClientRect.left >= 0 &&
						entry.isIntersecting;

					const th = entry.intersectionRatio;
					console.log('[Animator] entry', {
						key,
						intersectionRatio: th,
						isIntersecting: entry.isIntersecting,
						top: entry.boundingClientRect.top,
						left: entry.boundingClientRect.left,
						isEntering,
					});
					const closest = Array.from((isEntering ? entering : exiting).keys()).filter(
						e => Math.abs(e - th) < 0.08,
					);
					console.log('[Animator] closest thresholds', { key, closest, isEntering });
					const currentAnimations: any[] = [];

					for (let each of closest) {
						const animations = (isEntering ? entering : exiting).get(each)!;
						for (let animation of animations) {
							const key = animation.key;
							if (animation.numOfObservations < 1) currentAnimations.push(animation);
							else {
								if (!animationCount.current[key]) animationCount.current[key] = 1;
								if (animationCount.current[key] <= animation.numOfObservations) {
									currentAnimations.push(animation);
									animationCount.current[key]++;
								}
							}
						}
					}

					closest.flatMap(e => (isEntering ? entering : exiting).get(e));

					console.log('[Animator] applying animations', {
						key,
						count: currentAnimations.length,
						names: currentAnimations.map(a => a.animationName),
					});
					setObservations(currentAnimations);
				},
				{ threshold: Array.from(new Set(threshold)) },
			);
			io.observe(ref.current);
			console.log('[Animator] observing', { key });
			return () => (ref.current ? io.unobserve(ref.current!) : undefined);
		} catch (e) {
			console.error('[Animator] observer setup failed', { key, error: e });
		}
	}, [animation, ref.current, setObservations]);

	return (
		<div className="comp compAnimator" style={resolvedStyles.comp ?? {}} ref={ref}>
			<HelperComponent context={props.context} key={`${key}_hlp`} definition={definition} />
			<div
				className="_childContainer"
				style={{
					...(resolvedStyles.container ?? {}),
					animation: makeAnimationString([
						...animation,
						...observations.map(e => ({ ...e, observation: 'none' })),
					]),
				}}
			>
				<SubHelperComponent
					key={`${key}_shlp`}
					definition={definition}
					subComponentName="container"
				/>
				{childs}
			</div>
		</div>
	);
}

const component: Component = {
	name: 'Animator',
	displayName: 'Animator',
	description: 'Animator component',
	component: Animator,
	propertyValidation: (props: ComponentPropertyDefinition): Array<string> => [],
	properties: propertiesDefinition,
	styleComponent: AnimatorStyle,
	styleDefaults: styleDefaults,
	allowedChildrenType: new Map<string, number>([['', 1]]),
	styleProperties: stylePropertiesDefinition,
	defaultTemplate: {
		key: '',
		name: 'Animator',
		type: 'Animator',
	},
	stylePropertiesForTheme: styleProperties,
};

export default component;
